import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { DashboardLayout } from '../../../components/DashboardLayout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Plus, Pencil, Archive, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function V2Categories() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v2/categories`, { headers });
      setCategories(res.data);
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const openNew = () => { setEditCat({ name: '', description: '', status: 'active' }); setEditOpen(true); };
  const openEdit = (c) => { setEditCat({ ...c }); setEditOpen(true); };

  const handleSave = async () => {
    if (!editCat.name?.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editCat.id) {
        await axios.put(`${API_URL}/api/v2/categories/${editCat.id}`, editCat, { headers });
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/api/v2/categories`, editCat, { headers });
        toast.success('Category created');
      }
      setEditOpen(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/v2/categories/${id}`, { headers });
      toast.success('Category archived');
      fetchCategories();
    } catch (err) {
      toast.error('Failed to archive');
    }
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-4" data-testid="v2-categories">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">V2 Categories</h1>
          <Button onClick={openNew} data-testid="v2-add-category"><Plus className="w-4 h-4 mr-1.5" /> Add Category</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            [1,2,3].map(i => <Card key={i}><CardContent className="p-6 animate-pulse"><div className="h-20 bg-muted rounded" /></CardContent></Card>)
          ) : categories.length === 0 ? (
            <Card className="col-span-full"><CardContent className="p-8 text-center text-muted-foreground">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No categories yet. Create your first V2 category.</p>
            </CardContent></Card>
          ) : categories.map(c => (
            <Card key={c.id} className={c.status === 'archived' ? 'opacity-50' : ''}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{c.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{c.description || 'No description'}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant={c.status === 'active' ? 'default' : 'secondary'}>{c.status}</Badge>
                      <span className="text-xs text-muted-foreground">{c.question_count} question(s)</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="w-4 h-4" /></Button>
                    {c.status !== 'archived' && (
                      <Button variant="ghost" size="sm" onClick={() => handleArchive(c.id)}><Archive className="w-4 h-4 text-red-500" /></Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editCat?.id ? 'Edit Category' : 'New Category'}</DialogTitle></DialogHeader>
            {editCat && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Name *</label>
                  <Input value={editCat.name} onChange={e => setEditCat({...editCat, name: e.target.value})} data-testid="v2-cat-name" />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <textarea className="w-full mt-1 p-2 border rounded-md bg-background min-h-[60px] text-sm" value={editCat.description || ''} onChange={e => setEditCat({...editCat, description: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <select className="w-full mt-1 p-2 border rounded-md bg-background text-sm" value={editCat.status} onChange={e => setEditCat({...editCat, status: e.target.value})}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="v2-save-category">{saving ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
